// What only Google's reads have. Active users carry who is on the site now,
// from Google Analytics' realtime report: a live dot and a count beside the
// period's number, gone once that read is old. Search performance shows
// Search Console's four numbers together, each with its change against the
// period before, over one chart of the days: a click on a number puts its line
// on the chart or takes it off, each line on its own scale, and position upside
// down, since a lower position is better.

import { useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { formatNumber } from "../../shared/format";
import { periodName } from "../../shared/period";
import type { Stream } from "../../shared/types";
import { cx } from "../lib/util";
import { smoothPath, useSize } from "../charts/charts";
import type { WP } from "./data";
import { usePeriod } from "./DataControl";
import { quarter } from "./layout";
import { perfFit } from "./plans";
import { useTick } from "./tick";
import { FORMAT, KEYS, liveOf, NAME, TONE, type Key, type Perf, perfOf } from "./googleData";
import "../styles/google.css";

export { perfOf };

// ---------------------------------------------------------------------------
// who is on the site now

/** "● 12 now": active users in the last 30 minutes, from Google Analytics' realtime report. */
export function Live({ stream }: { stream: Stream }) {
  useTick(60_000);
  const live = liveOf(stream);
  if (!live) return null;
  return (
    <span className={cx("lv", live.users === 0 && "is-quiet")} title={`${formatNumber(live.users, null)} active in the last ${live.minutes} minutes, from Google Analytics' realtime report`}>
      <i />
      {nowWords(stream)}
    </span>
  );
}

/** What the now count says ("12 now") while Google Analytics' realtime count is fresh, so a widget can make room for it. */
export function nowWords(stream: Stream): string | null {
  const live = liveOf(stream);
  return live ? `${formatNumber(live.users, null)} now` : null;
}

// ---------------------------------------------------------------------------
// Search performance

/** One of the four numbers: its name in its line's color, the number, and its change against the period before. */
function Cell({ k, p, on, days, onToggle }: { k: Key; p: Perf; on: boolean; days: number; onToggle: (() => void) | null }) {
  const { total, previous } = p.all[k];
  const abs = previous === null ? null : total - previous;
  const pct = previous ? ((total - previous) / Math.abs(previous)) * 100 : null;
  // a rate changes by points, a position by places
  const delta = abs === null ? null : k === "ctr" ? `${formatNumber(Math.abs(abs), { style: "number", decimals: Math.abs(abs) < 1 ? 2 : 1 })} pts` : k === "position" || pct === null || Math.abs(pct) >= 10000 ? formatNumber(Math.abs(abs), FORMAT[k]) : `${Math.abs(pct) >= 10 ? Math.abs(pct).toFixed(0) : Math.abs(pct).toFixed(1)}%`;
  // a change too small to show is no change; a lower position is better, and position rises as its number falls, as its line does
  const flat = abs === 0 || (delta !== null && !/[1-9]/.test(delta));
  const good = abs === null || flat ? null : k === "position" ? abs < 0 : abs > 0;
  const Icon = flat ? Minus : abs !== null && abs > 0 !== (k === "position") ? ArrowUpRight : ArrowDownRight;
  const body = (
    <>
      <span className="pf-name">
        <i style={{ background: `var(--chart-${TONE[k]})` }} />
        {NAME[k]}
      </span>
      <b className="pf-v">{formatNumber(total, FORMAT[k])}</b>
      {delta !== null ? (
        <span className={cx("pf-chip", good === true && "is-good", good === false && "is-bad")} title={`vs the ${periodName(days).toLowerCase()} before: ${formatNumber(previous!, FORMAT[k])}`}>
          <Icon strokeWidth={2.4} />
          {delta}
        </span>
      ) : (
        <span className="pf-chip is-none">No period before</span>
      )}
    </>
  );
  return onToggle ? (
    <button type="button" className={cx("pf-cell", on && "is-on")} aria-pressed={on} title={on ? `Hide ${NAME[k].toLowerCase()} on the chart` : `Show ${NAME[k].toLowerCase()} on the chart`} onClick={onToggle} onPointerDown={(e) => e.stopPropagation()} data-interactive>
      {body}
    </button>
  ) : (
    <div className="pf-cell">{body}</div>
  );
}

/** The chosen lines over the period's days, each on its own scale (position upside down), with the day under the pointer. */
function PerfChart({ p, on, scale }: { p: Perf; on: Key[]; scale: number }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const { w: W, h: H } = size;
  const padT = Math.round(0.5 * scale);
  const padB = Math.round(1.55 * scale);
  const lines = useMemo(() => {
    if (p.days.length < 2 || W <= 0 || H <= 0) return [];
    const ih = H - padT - padB;
    const x = (i: number) => (i / (p.days.length - 1)) * (W - 2) + 1;
    return on.map((k) => {
      let lo = Infinity;
      let hi = -Infinity;
      for (const d of p.days) {
        lo = Math.min(lo, d.v[k]);
        hi = Math.max(hi, d.v[k]);
      }
      if (hi === lo) {
        hi += Math.abs(hi || 1) * 0.5;
        lo -= Math.abs(lo || 1) * 0.5;
      }
      const pad = (hi - lo) * 0.1;
      const y = (v: number) => padT + (k === "position" ? (v - lo + pad) / (hi - lo + 2 * pad) : 1 - (v - lo + pad) / (hi - lo + 2 * pad)) * ih;
      const xy = p.days.map((d, i) => [x(i), y(d.v[k])] as [number, number]);
      return { k, xy, d: smoothPath(xy) };
    });
  }, [p, on, W, H, padT, padB]);
  if (p.days.length < 2) return <div className="pf-plot pf-empty">Not enough days to draw yet</div>;
  const fmtDay = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const hx = hover !== null && lines[0] ? lines[0].xy[hover][0] : null;
  return (
    <div
      ref={ref}
      className="pf-plot"
      data-interactive-hover
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const f = (e.clientX - r.left) / Math.max(1, r.width);
        setHover(Math.max(0, Math.min(p.days.length - 1, Math.round(f * (p.days.length - 1)))));
      }}
      onPointerLeave={() => setHover(null)}
    >
      {W > 0 && (
        <svg width={W} height={H}>
          <line className="pf-base" x1={0} x2={W} y1={H - padB + 0.5} y2={H - padB + 0.5} />
          {lines.map((l) => (
            <path key={l.k} d={l.d} className="pf-line" style={{ stroke: `var(--chart-${TONE[l.k]})` }} />
          ))}
          {hx !== null && <line className="pf-guide" x1={hx} x2={hx} y1={padT} y2={H - padB} />}
          {hover !== null && lines.map((l) => <circle key={l.k} cx={l.xy[hover][0]} cy={l.xy[hover][1]} r={0.26 * scale} className="pf-dot" style={{ fill: `var(--chart-${TONE[l.k]})` }} />)}
          <text className="pf-date" x={1} y={H - 0.35 * scale} textAnchor="start">
            {fmtDay(p.days[0].t)}
          </text>
          <text className="pf-date" x={W - 1} y={H - 0.35 * scale} textAnchor="end">
            {fmtDay(p.days[p.days.length - 1].t)}
          </text>
        </svg>
      )}
      {hover !== null && hx !== null && (
        <div className="chart-tip pf-tip" style={{ left: Math.min(Math.max(hx, 70), W - 70), top: 0 }}>
          <span>{fmtDay(p.days[hover].t)}</span>
          {on.map((k) => (
            <b key={k}>
              <i style={{ background: `var(--chart-${TONE[k]})` }} />
              {formatNumber(p.days[hover].v[k], FORMAT[k])} {NAME[k].toLowerCase()}
            </b>
          ))}
        </div>
      )}
    </div>
  );
}

/** Search performance: the four numbers with their changes, over a chart of the ones chosen; the numbers alone in a small tile. */
export function Performance({ item, stream, readOnly }: WP) {
  const s = stream!;
  const p = perfOf(s)!;
  const [ref, size] = useSize<HTMLDivElement>();
  const per = usePeriod(item, s);
  const [on, setOn] = useState<Key[]>(["clicks", "impressions"]);
  const fit = perfFit(size.w ? size : { w: item.w - 28, h: item.h - 52 });
  const days = s.period?.days ?? per.days;
  const reading = per.pending !== null || (per.source !== null && s.period !== null && s.period !== undefined && s.period.days !== per.source.days);
  const toggle = (k: Key) => setOn((v) => (v.includes(k) ? (v.length > 1 ? v.filter((x) => x !== k) : v) : KEYS.filter((x) => v.includes(x) || x === k)));
  return (
    <div className={cx("pf", `is-${fit.id}`, reading && "is-reading")} ref={ref} style={{ fontSize: quarter(fit.px) }}>
      <div className="pf-cells">
        {KEYS.map((k) => (
          <Cell key={k} k={k} p={p} on={fit.id === "row" && on.includes(k)} days={days} onToggle={fit.id === "row" && !readOnly ? () => toggle(k) : null} />
        ))}
      </div>
      {fit.id === "row" && <PerfChart p={p} on={on} scale={fit.px} />}
    </div>
  );
}
